import { Sparkles } from "@/components/icons";
import { PRODUCT_TYPES, type ProductType } from "@/lib/generated/product-types";
import { Image } from "@/components/ui/image";
import { SelectableCard } from "@/components/ui/selectable-card";

interface ProductTypeHintSelectorProps {
  selectedSlug: string | null;
  onSelect: (slug: string | null) => void;
  showLabel?: boolean;
}

export function ProductTypeHintSelector({
  selectedSlug,
  onSelect,
  showLabel = true,
}: ProductTypeHintSelectorProps) {
  const mainProducts = PRODUCT_TYPES.filter(
    (p) => p.isFeatured || p.displayOrder <= 10,
  );

  return (
    <div className="space-y-3">
      {showLabel && (
        <p className="text-sm text-muted-foreground font-medium">
          Select product type (optional)
        </p>
      )}
      <div className="@container/hints">
        <div className="grid grid-cols-2 gap-2 @sm/hints:grid-cols-3 @xl/hints:grid-cols-4 @3xl/hints:grid-cols-5">
          <TileButton
            selected={selectedSlug === null}
            onClick={() => onSelect(null)}
            icon={<Sparkles className="h-5 w-5" />}
            label="Auto-detect"
          />
          {mainProducts.map((product) => (
            <ProductTileButton
              key={product.slug}
              product={product}
              selected={selectedSlug === product.slug}
              onClick={() => onSelect(product.slug)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TileButton({
  selected,
  onClick,
  icon,
  label,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <SelectableCard
      size="compact"
      isSelected={selected}
      aria-pressed={selected}
      onClick={onClick}
      className="text-xs font-medium"
    >
      {icon}
      <span className="truncate w-full text-center">{label}</span>
    </SelectableCard>
  );
}

function ProductTileButton({
  product,
  selected,
  onClick,
}: {
  product: ProductType;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <SelectableCard
      size="compact"
      isSelected={selected}
      aria-pressed={selected}
      onClick={onClick}
      className="text-xs font-medium"
    >
      {product.iconUrl ? (
        <Image
          src={product.iconUrl}
          alt=""
          width={20}
          height={20}
          className="rounded-sm"
        />
      ) : (
        <div className="h-5 w-5" />
      )}
      <span className="truncate w-full text-center">
        {product.pluralDisplayName}
      </span>
    </SelectableCard>
  );
}
